/**
 * Tree query and retrieval functions
 * Functions for safely accessing, searching, and navigating the tree structure
 */

import { TodoTree, TodoItem, findRootNodeId } from '../../types/todo';

/**
 * Safely retrieves a todo item by its ID
 * @param tree - The todo tree structure
 * @param id - ID of the item to retrieve
 * @returns The todo item or null if not found
 */
export function getItemById(tree: TodoTree, id: string): TodoItem | null {
  return tree[id] || null;
}

/**
 * Gets all valid children of a parent node
 * Filters out any children that don't exist in the tree (data integrity protection)
 * @param tree - The todo tree structure
 * @param parentId - ID of the parent node
 * @returns Array of child objects with their IDs and items
 */
export function getChildrenItems(tree: TodoTree, parentId: string): Array<{ id: string; item: TodoItem }> {
  const parent = getItemById(tree, parentId);
  if (!parent) {
    return [];
  }

  return parent.children
    .map(childId => {
      const item = getItemById(tree, childId);
      return item ? { id: childId, item } : null;
    })
    .filter((child): child is { id: string; item: TodoItem } => child !== null);
}

/**
 * Gets the path from root to a target node
 * @param tree - The todo tree structure
 * @param targetId - ID of the target node
 * @param rootId - Optional root node ID
 * @returns Array of node IDs representing the path, or null if not found
 */
export function getItemPath(tree: TodoTree, targetId: string, rootId?: string): string[] | null {
  const actualRootId = rootId || findRootNodeId(tree);
  if (!actualRootId) {
    return null;
  }

  function findPath(currentId: string, path: string[]): string[] | null {
    const currentPath = [...path, currentId];

    if (currentId === targetId) {
      return currentPath;
    }

    const item = getItemById(tree, currentId);
    if (!item) {
      return null;
    }

    for (const childId of item.children) {
      const result = findPath(childId, currentPath);
      if (result) {
        return result;
      }
    }

    return null;
  }

  return findPath(actualRootId, []);
}
